'use client';

import { useEffect, useState } from 'react';
import rte from '@/utils/rte';
import Block from '@/components/elements/Block';
import Container from '@/components/elements/Container';
import Heading2 from '@/components/elements/Heading2';
import CTAButton from '@/components/elements/CTAButton';
import { openChat } from '@/components/integrations/Kodif/Actions';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ChevronIcon = ({ open }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const renderAnswer = (answer) => {
  if (!answer) return '';
  try {
    return rte(answer) ?? '';
  } catch {
    return '';
  }
};

const Faq = ({ block = {}, position }) => {
  const { heading, tabs = [], below_tabs_content, settings = {} } = block;

  const safeTabs = Array.isArray(tabs) ? tabs : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileCollapsed, setMobileCollapsed] = useState(false);
  const clampedActive = Math.min(activeIndex, Math.max(safeTabs.length - 1, 0));
  const activeTab = safeTabs[clampedActive] ?? null;
  const questions = activeTab?.questions_and_answers ?? [];

  const [openIndexes, setOpenIndexes] = useState(() => (questions.length > 0 ? [0] : []));

  useEffect(() => {
    setOpenIndexes(questions.length > 0 ? [0] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedActive]);

  const toggleQuestion = (i) => {
    setOpenIndexes((prev) =>
      prev.includes(i) ? [] : [i]
    );
  };

  const handleTabClick = (i) => {
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches;
    if (isMobile && i === activeIndex && !mobileCollapsed) {
      setMobileCollapsed(true);
    } else {
      setActiveIndex(i);
      setMobileCollapsed(false);
    }
  };

  const textColor = settings?.text_color?.color_selector;
  const sectionId = `faq-${block?._metadata?.uid || (position ?? 'default')}`;

  const footerLabel = below_tabs_content?.label;
  const chatLabel = below_tabs_content?.web_link?.label || 'Chat Now';
  const hasFooter = !!(below_tabs_content && (footerLabel || below_tabs_content?.web_link));

  const renderQuestions = (scope = 'd') => {
    if (questions.length === 0) {
      return <p className="font-worksans text-primary/60 text-sm py-5">No FAQs available.</p>;
    }
    return questions.map((qa, i) => {
      const open = openIndexes.includes(i);
      const headingId = `${sectionId}-q-${scope}-${clampedActive}-${i}`;
      const panelId = `${sectionId}-a-${scope}-${clampedActive}-${i}`;
      return (
        <div key={qa?._metadata?.uid ?? i}>
          <button
            type="button"
            onClick={() => toggleQuestion(i)}
            aria-expanded={open}
            aria-controls={panelId}
            id={headingId}
            className="w-full flex items-center justify-between text-left py-5 gap-6 group cursor-pointer"
          >
            <span className="font-worksans text-primary text-base font-bold leading-snug">
              {qa?.question}
            </span>
            <span
              aria-hidden="true"
              className="shrink-0 text-primary leading-none w-4 text-center select-none"
            >
              {open ? <XMarkIcon strokeWidth={2.5} /> : <PlusIcon strokeWidth={2.5} />}
            </span>
          </button>
          {qa?.answer && (
            <div
              id={panelId}
              role="region"
              aria-labelledby={headingId}
              className={`overflow-hidden ${open ? 'transition-[max-height,opacity] duration-300 ease-out max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
            >
              <div
                className="faq-answer pb-5 font-worksans text-primary/80 text-sm leading-relaxed transition-all duration-300 ease-out [&_a]:underline [&_a]:underline-offset-2 [&_a]:text-sea [&_a:hover]:opacity-70 [&_p]:mb-3 last:[&_p]:mb-0"
                dangerouslySetInnerHTML={{ __html: renderAnswer(qa.answer) }}
              />
            </div>
          )}
        </div>
      );
    });
  };

  const renderFooterCTA = () => (
    <div className="flex items-center gap-2">
      {footerLabel && (
        <p className="font-worksans text-sm font-semibold text-primary whitespace-nowrap">
          {footerLabel}
        </p>
      )}
      <CTAButton
        variant="invert"
        onClick={openChat}
        className="font-worksans text-lg px-6 py-3 whitespace-nowrap shrink-0"
      >
        {chatLabel}
      </CTAButton>
    </div>
  );

  return (
    <Block settings={settings} name="faq" id={sectionId}>
      <Container>
        {heading && <Heading2 color={textColor} className="text-4xl">{heading}</Heading2>}

        {safeTabs.length > 0 && (
          <>
            <div className="md:grid md:grid-cols-[260px_1fr] md:gap-8 md:items-start mt-6">
              {/* Category rail — mobile: full-width accordion; desktop: left column selector */}
              <div>
                <div className="flex flex-col gap-2">
                  {safeTabs.map((tab, i) => {
                    const label = tab?.tab_label || `Tab ${i + 1}`;
                    const isActive = i === clampedActive;
                    const isOpenOnMobile = isActive && !mobileCollapsed;
                    const tabId = `${sectionId}-tab-${i}`;
                    const mobilePanelId = `${sectionId}-panel-m-${i}`;
                    return (
                      <div key={tab?._metadata?.uid ?? i}>
                        <button
                          id={tabId}
                          type="button"
                          aria-expanded={isOpenOnMobile}
                          aria-controls={mobilePanelId}
                          onClick={() => handleTabClick(i)}
                          className={`w-full font-worksans text-left px-5 py-4 border-2 text-sm transition-colors flex items-center justify-between gap-4 ${
                            isOpenOnMobile
                              ? 'max-md:border-sea max-md:text-primary max-md:font-medium'
                              : 'max-md:border-[#d9d3cd] max-md:text-primary'
                          } ${
                            isActive
                              ? 'md:border-sea md:text-primary md:font-medium'
                              : 'md:border-[#d9d3cd] md:text-primary md:hover:border-sea md:hover:text-primary'
                          }`}
                        >
                          <span>{label}</span>
                          <span className="md:hidden shrink-0 text-primary/70">
                            <ChevronIcon open={isOpenOnMobile} />
                          </span>
                        </button>
                        <div
                          id={mobilePanelId}
                          role="region"
                          aria-labelledby={tabId}
                          className={`px-8 ${isOpenOnMobile ? 'md:hidden' : 'hidden'}`}
                        >
                          {isOpenOnMobile && renderQuestions('m')}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {hasFooter && (
                  <div className="hidden md:block mt-6">
                    {renderFooterCTA()}
                  </div>
                )}
              </div>

              {/* Right rail — desktop only */}
              <div
                id={`${sectionId}-panel-d`}
                role="region"
                aria-labelledby={`${sectionId}-tab-${clampedActive}`}
                className="hidden md:block"
              >
                {renderQuestions('d')}
              </div>
            </div>

            {hasFooter && (
              <div className="md:hidden mt-6">{renderFooterCTA()}</div>
            )}
          </>
        )}
      </Container>
    </Block>
  );
};

export default Faq;